import { environment } from '../env/environment';
import { compressImage, compressBase64Image } from './imageCompressor';
import logger from '../utils/logger';

// Configuration for optimized upload
const USE_ASYNC_UPLOAD = true; // Enable async image upload for faster response
const COMPRESS_IMAGES = true; // Enable client-side image compression

/**
 * Get current tenant ID from various sources
 */
const getCurrentTenantId = () => {
  // Try to get from window (set by TenantContext)
  if (window.__TENANT_ID__) {
    return window.__TENANT_ID__;
  }

  // Try localStorage for development
  const devTenant = localStorage.getItem('dev_tenant_id');
  if (devTenant) {
    return devTenant;
  }

  return null;
};

// Helper function to convert base64 to Blob
const base64ToBlob = (base64String, fieldName) => {
  try {
    // Extract the content type and base64 data
    const matches = base64String.match(/^data:([^;]+);base64,(.+)$/);
    if (!matches) return null;

    const contentType = matches[1];
    const base64Data = matches[2];

    // Convert base64 to binary
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    // Create blob
    const blob = new Blob([byteArray], { type: contentType });

    // Determine file extension
    const ext = contentType.split('/')[1] || 'png';
    const fileName = `${fieldName}_${Date.now()}.${ext}`;

    // Convert to File
    return new File([blob], fileName, { type: contentType });
  } catch (error) {
    logger.error('Error converting base64 to blob:', error);
    return null;
  }
};

// Helper to check if string is base64 image
const isBase64Image = (value) => {
  if (typeof value !== 'string') return false;
  return value.startsWith('data:image/');
};

export const sendToBackend = async (pdfBlob, formData, painDiagramPdf, painLevels, options = {}) => {
  const sendData = new FormData();
  const useAsync = options.useAsync ?? USE_ASYNC_UPLOAD;
  const shouldCompress = options.compress ?? COMPRESS_IMAGES;

  // Add tenant ID for multi-tenant support
  const tenantId = options.tenantId || getCurrentTenantId();
  if (tenantId) {
    sendData.append('tenant_id', tenantId);
  }

  if (pdfBlob instanceof Blob) {
    sendData.append('pdf', pdfBlob, 'patient_form.pdf');
  }

  if (painDiagramPdf instanceof File) {
    sendData.append('painDiagramPdf', painDiagramPdf, painDiagramPdf.name);
  }

  Object.keys(painLevels || {}).forEach(key => {
    sendData.append(`painLevel_${key}`, painLevels[key]);
  });

  const processedFormData = { ...formData };
  let imageCounter = 0;

  // Process form data with async image handling
  const imagePromises = [];

  for (const key of Object.keys(formData)) {
    let value = formData[key];

    if (
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray(value) && value.length === 0)
    ) {
      continue;
    }

    // Handle base64 image strings - compress and convert to File
    if (isBase64Image(value)) {
      imagePromises.push(
        (async () => {
          try {
            // Compress base64 image if enabled
            const processedBase64 = shouldCompress
              ? await compressBase64Image(value, { maxWidth: 1280, quality: 0.7, maxSizeKB: 200 })
              : value;

            const file = base64ToBlob(processedBase64, key);
            if (file) {
              sendData.append(key, file, file.name);
              imageCounter++;
              // Store only metadata (not base64) - images uploaded via Google Drive
              processedFormData[key] = {
                name: file.name,
                type: file.type,
                size: file.size,
                _uploadPending: true
              };
            }
          } catch (err) {
            logger.warn(`Failed to process image ${key}:`, err);
            // Fallback to original
            const file = base64ToBlob(value, key);
            if (file) {
              sendData.append(key, file, file.name);
              imageCounter++;
              // Store only metadata
              processedFormData[key] = {
                name: file.name,
                type: file.type,
                size: file.size,
                _uploadPending: true
              };
            }
          }
        })()
      );
      continue;
    }

    // Handle File arrays - compress each file and store only metadata
    if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
      imagePromises.push(
        (async () => {
          const fileMetas = [];

          for (const file of value) {
            if (file.type.startsWith('image/')) {
              try {
                const processedFile = shouldCompress
                  ? await compressImage(file, { maxWidth: 1280, quality: 0.7, maxSizeKB: 200 })
                  : file;
                sendData.append(key, processedFile, processedFile.name);
                imageCounter++;

                // Store only metadata - images will be uploaded to Google Drive
                fileMetas.push({
                  name: processedFile.name,
                  type: processedFile.type,
                  size: processedFile.size,
                  _uploadPending: true
                });
              } catch (err) {
                logger.warn(`Failed to compress ${file.name}:`, err);
                sendData.append(key, file, file.name);
                imageCounter++;

                fileMetas.push({
                  name: file.name,
                  type: file.type,
                  size: file.size,
                  _uploadPending: true
                });
              }
            }
          }

          // Store only metadata (not base64) for database storage
          processedFormData[key] = fileMetas;

          sendData.append(`${key}_meta`, JSON.stringify(value.map(file => ({
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified
          }))));
        })()
      );
      continue;
    }

    // Handle DocumentUpload objects (e.g. { dl_front: { fileId, preview, ... }, dl_back: { ... } })
    // Pre-uploaded docs have fileId — send only metadata, no binary data
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const subKeys = Object.keys(value);
      const hasPreUploaded = subKeys.some(sk => value[sk]?.fileId);
      const hasDocFiles = subKeys.some(sk => value[sk]?.file instanceof File);

      if (hasPreUploaded || hasDocFiles) {
        const docMeta = {};
        for (const subKey of subKeys) {
          const entry = value[subKey];
          if (entry?.fileId) {
            docMeta[subKey] = {
              fileId: entry.fileId,
              name: entry.name,
              size: entry.compressedSize || 0,
            };
          } else if (entry?.file instanceof File) {
            const fieldName = `${key}_${subKey}`;
            const processedFile = shouldCompress
              ? await compressImage(entry.file, { maxWidth: 1280, quality: 0.7, maxSizeKB: 200 })
              : entry.file;
            sendData.append(fieldName, processedFile, processedFile.name);
            imageCounter++;
            docMeta[subKey] = {
              name: processedFile.name,
              type: processedFile.type,
              size: processedFile.size,
            };
          }
        }
        sendData.append(`${key}_meta`, JSON.stringify(docMeta));
        processedFormData[key] = docMeta;
        continue;
      }

      // Regular object — JSON stringify
      sendData.append(key, JSON.stringify(value));
    } else if (Array.isArray(value)) {
      sendData.append(key, JSON.stringify(value));
    } else {
      sendData.append(key, value);
    }
  }

  // Wait for all image processing to complete
  await Promise.all(imagePromises);

  sendData.append('totalImages', imageCounter.toString());

  // Choose endpoint — use baseUrl (direct cross-origin to API server)
  // NOT uploadUrl (SSR proxy) which has platform body-size limits
  const endpoint = useAsync
    ? `${environment.baseUrl}/api/drive/upload-patient-form-async`
    : `${environment.baseUrl}/api/drive/upload-patient-form`;

  const headers = {};
  if (tenantId) {
    headers['X-Tenant-ID'] = String(tenantId);
  }

  const MAX_RETRIES = 2;
  // Large multipart uploads (ID photos, insurance cards, PDFs) on slow mobile
  // connections can legitimately take a few minutes to finish sending. 120s was
  // too short and aborted valid uploads mid-flight ("Submission timed out").
  const SUBMIT_TIMEOUT_MS = 300000; // 5 minutes
  let lastError;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), SUBMIT_TIMEOUT_MS);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: sendData,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let serverMessage = `Failed to upload form: ${response.status}`;
        try {
          const errBody = await response.json();
          if (errBody.errors && Array.isArray(errBody.errors)) {
            serverMessage = errBody.errors.join('. ');
          } else if (errBody.message) {
            serverMessage = errBody.message;
          }
        } catch (_) { /* response wasn't JSON */ }

        if (response.status >= 500 && attempt < MAX_RETRIES) {
          logger.warn(`[Submit] Server error ${response.status}, retrying (${attempt + 1}/${MAX_RETRIES})...`);
          await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
          continue;
        }
        throw new Error(serverMessage);
      }

      const result = await response.json();

      if (useAsync && result.asyncProcessing?.jobId) {
        result.checkStatus = () => checkUploadStatus(result.asyncProcessing.jobId);
      }

      return result;
    } catch (error) {
      lastError = error;
      if (error.name === 'AbortError') {
        throw new Error('Submission timed out. Please check your connection and try again.');
      }
      if (attempt < MAX_RETRIES && error.message?.includes('Load failed')) {
        logger.warn(`[Submit] Network error, retrying (${attempt + 1}/${MAX_RETRIES})...`);
        await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
        continue;
      }
      logger.error('Error sending form data:', error);
      throw error;
    }
  }
  throw lastError;
};

/**
 * Check the status of an async upload job
 * @param {string} jobId - The job ID returned from async upload
 * @returns {Promise<Object>} - Job status
 */
export const checkUploadStatus = async (jobId) => {
  try {
    const response = await fetch(
      `${environment.baseUrl}/api/drive/upload-status/${jobId}`
    );

    if (!response.ok) {
      throw new Error(`Failed to check status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Error checking upload status:', error);
    throw error;
  }
};
